/**
 * AlgorithmShield entry point.
 *
 * Wires: file ingestion → canvas audit → repair actions → live mobile grid
 * simulator → freemium gate → batch ZIP export, plus PWA service-worker
 * registration. Everything runs in the browser; nothing is uploaded.
 */

import './style.css';

import { renderApp } from './app/render.ts';
import { store, type AppState, type ProductImage, type ToastKind } from './app/store.ts';
import {
  auditImagePixels,
  resizePixels,
  snapNearWhiteToPureWhite,
  COMPLIANCE_THRESHOLDS,
  MOBILE_THUMB_SIZE
} from './utils/image-auditor.ts';
import { autoCropAndPadToFill, fitIntoPreset } from './utils/image-ops.ts';
import {
  MARKETPLACE_PRESETS,
  checkBatchQuota,
  createBatchZip,
  extensionForFormat,
  generateBatchFilename,
  slugifySku,
  textEntry,
  type ExportFormat,
  type MarketplaceId,
  type ZipFileInput
} from './utils/batch-exporter.ts';
import { generateLicenseKey, validateLicenseKey } from './utils/license.ts';
import { stripExif } from './utils/exif.ts';
import {
  blobToBytes,
  downloadBytes,
  fileToImageData,
  getContext2d,
  pixelsToBlob,
  putPixelsAt
} from './utils/canvas-io.ts';

const rootElement = document.getElementById('app');
if (!rootElement) throw new Error('Missing #app mount point');
const root: HTMLElement = rootElement;

const LICENSE_STORAGE_KEY = 'algorithmshield.license';
const MAX_DECODE_DIMENSION = 4096;
const DEMO_LICENSE_SEED = 1337;

let toastTimer: ReturnType<typeof setTimeout> | null = null;

/* -------------------------------------------------------------------------- */
/* Rendering                                                                  */
/* -------------------------------------------------------------------------- */

function render(state: AppState): void {
  root.innerHTML = renderApp(state);
  paintCanvases(state);
}

function subscribe(): void {
  store.subscribe((state) => render(state));
  render(store.getState());
}

function showToast(kind: ToastKind, text: string): void {
  const toast = { kind, text };
  store.set({ toast });
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    if (store.getState().toast === toast) store.set({ toast: null });
  }, 4000);
}

/* -------------------------------------------------------------------------- */
/* Canvas painting                                                            */
/* -------------------------------------------------------------------------- */

function paintCanvases(state: AppState): void {
  paintPreviews(state);
  paintSimulatorThumb();
}

function paintPreviews(state: AppState): void {
  const canvases = root.querySelectorAll<HTMLCanvasElement>('canvas[data-preview]');
  for (const canvas of canvases) {
    const id = canvas.dataset.preview ?? '';
    const image = state.images.find((item) => item.id === id);
    if (!image) continue;

    // Fixed white preview frame matching the CSS box (w-full x max-h-52 = 208px).
    const frameWidth = Math.max(240, canvas.clientWidth || 360);
    const frameHeight = 208;
    canvas.width = frameWidth;
    canvas.height = frameHeight;

    const ctx = getContext2d(canvas);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, frameWidth, frameHeight);

    // Letterbox the product inside the frame (marketplaces show white margins).
    const scale = Math.min(frameWidth / image.workingWidth, frameHeight / image.workingHeight);
    const outW = Math.max(1, Math.round(image.workingWidth * scale));
    const outH = Math.max(1, Math.round(image.workingHeight * scale));
    const offsetX = Math.round((frameWidth - outW) / 2);
    const offsetY = Math.round((frameHeight - outH) / 2);

    const scaled = resizePixels(image.workingPixels, image.workingWidth, image.workingHeight, outW, outH);
    const temp = document.createElement('canvas');
    temp.width = outW;
    temp.height = outH;
    putPixelsAt(getContext2d(temp), scaled.pixels, outW, outH);
    ctx.drawImage(temp, offsetX, offsetY);

    // Bounding-box overlay: emerald when compliant, rose when at risk.
    const box = image.report.boundingBox;
    const pxScaleX = outW / image.workingWidth;
    const pxScaleY = outH / image.workingHeight;
    ctx.lineWidth = Math.max(2, frameWidth / 180);
    ctx.setLineDash([ctx.lineWidth * 3, ctx.lineWidth * 2]);
    ctx.strokeStyle = image.report.isAmazonCompliant ? '#10b981' : '#f43f5e';
    ctx.strokeRect(
      offsetX + box.minX * pxScaleX,
      offsetY + box.minY * pxScaleY,
      box.width * pxScaleX,
      box.height * pxScaleY
    );
    ctx.setLineDash([]);
  }
}

function paintSimulatorThumb(): void {
  const canvas = document.getElementById('sim-user-thumb') as HTMLCanvasElement | null;
  if (!canvas) return;
  const image = store.getActive();
  if (!image) return;

  const fitted = fitIntoPreset(
    image.workingPixels,
    image.workingWidth,
    image.workingHeight,
    MOBILE_THUMB_SIZE,
    MOBILE_THUMB_SIZE
  );
  canvas.width = MOBILE_THUMB_SIZE;
  canvas.height = MOBILE_THUMB_SIZE;
  putPixelsAt(getContext2d(canvas), fitted.pixels, fitted.width, fitted.height);
}

/* -------------------------------------------------------------------------- */
/* Image ingestion                                                            */
/* -------------------------------------------------------------------------- */

async function decodeFile(file: File): Promise<{ pixels: Uint8ClampedArray; width: number; height: number }> {
  const imageData = await fileToImageData(file);
  let { width, height } = imageData;
  let pixels: Uint8ClampedArray = imageData.data;

  if (width > MAX_DECODE_DIMENSION || height > MAX_DECODE_DIMENSION) {
    const scale = Math.min(MAX_DECODE_DIMENSION / width, MAX_DECODE_DIMENSION / height);
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));
    const resized = resizePixels(pixels, width, height, targetW, targetH);
    pixels = resized.pixels;
    width = resized.width;
    height = resized.height;
  }

  return { pixels, width, height };
}

async function addFiles(fileList: FileList | File[] | null): Promise<void> {
  if (!fileList) return;
  const files = Array.from(fileList).filter((file) => file.type.startsWith('image/'));
  if (files.length === 0) {
    showToast('error', 'No supported images found. Use JPG, PNG or WebP.');
    return;
  }

  const added: ProductImage[] = [];
  let failed = 0;

  for (const file of files) {
    try {
      const { pixels, width, height } = await decodeFile(file);
      const report = auditImagePixels(pixels, width, height);
      added.push({
        id: store.nextImageId(),
        fileName: file.name,
        originalPixels: new Uint8ClampedArray(pixels),
        originalWidth: width,
        originalHeight: height,
        workingPixels: pixels,
        workingWidth: width,
        workingHeight: height,
        report,
        snapped: false,
        autoFilled: false
      });
    } catch {
      failed++;
    }
  }

  if (added.length > 0) {
    const first = store.getState().images.length === 0;
    store.updateImages((images) => [...images, ...added]);
    if (first || !store.getState().activeId) store.set({ activeId: added[0].id });
  }

  if (failed > 0) showToast('error', `${failed} file${failed > 1 ? 's' : ''} could not be decoded.`);
  if (added.length > 0) showToast('success', `${added.length} image${added.length > 1 ? 's' : ''} audited locally.`);
}

function recomputeReport(image: ProductImage): ProductImage {
  return {
    ...image,
    report: auditImagePixels(image.workingPixels, image.workingWidth, image.workingHeight)
  };
}

function mutateImage(id: string, updater: (image: ProductImage) => ProductImage): void {
  store.updateImages((images) => images.map((image) => (image.id === id ? updater(image) : image)));
}

/* -------------------------------------------------------------------------- */
/* Repair actions                                                             */
/* -------------------------------------------------------------------------- */

function snapWhite(id: string): void {
  mutateImage(id, (image) => {
    const pixels = snapNearWhiteToPureWhite(image.workingPixels);
    return recomputeReport({ ...image, workingPixels: pixels, snapped: true });
  });
  showToast('success', 'Near-white (>240) pixels snapped to pure #FFFFFF.');
}

function applyAutoFill(id: string): void {
  const image = store.getState().images.find((item) => item.id === id);
  if (!image) return;

  if (!store.getState().isPro) {
    showToast('info', 'Auto 86% bounding-box fill scaling is a Pro feature.');
    focusLicense();
    return;
  }

  const result = autoCropAndPadToFill(
    image.workingPixels,
    image.workingWidth,
    image.workingHeight,
    COMPLIANCE_THRESHOLDS.autoFillTarget
  );

  if (!result.applied) {
    showToast('info', 'This image already matches the target fill.');
    return;
  }

  mutateImage(id, (current) =>
    recomputeReport({
      ...current,
      workingPixels: result.pixels,
      workingWidth: result.width,
      workingHeight: result.height,
      autoFilled: true
    })
  );

  showToast(
    'success',
    result.note
      ? `Fill scaled to ${result.achievedFill}% (${result.note})`
      : `Auto-cropped & padded to ${result.achievedFill}% fill (target ${result.targetFill}%).`
  );
}

function resetImage(id: string): void {
  mutateImage(id, (image) =>
    recomputeReport({
      ...image,
      workingPixels: new Uint8ClampedArray(image.originalPixels),
      workingWidth: image.originalWidth,
      workingHeight: image.originalHeight,
      snapped: false,
      autoFilled: false
    })
  );
  showToast('info', 'Image restored to its original pixels.');
}

function snapAll(): void {
  let changed = 0;
  store.updateImages((images) =>
    images.map((image) => {
      if (image.report.perimeterWhiteScore >= 100) return image;
      changed++;
      return recomputeReport({
        ...image,
        workingPixels: snapNearWhiteToPureWhite(image.workingPixels),
        snapped: true
      });
    })
  );
  showToast(changed > 0 ? 'success' : 'info', changed > 0 ? `Snapped near-white pixels in ${changed} image${changed > 1 ? 's' : ''}.` : 'All perimeters are already pure white.');
}

/* -------------------------------------------------------------------------- */
/* License / freemium                                                         */
/* -------------------------------------------------------------------------- */

function focusLicense(): void {
  const input = document.getElementById('license-key') as HTMLInputElement | null;
  input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  input?.focus();
}

function activateLicense(rawKey: string): void {
  const result = validateLicenseKey(rawKey);
  store.set({
    licenseInput: rawKey,
    isPro: result.valid,
    licenseMessage: result.message,
    licenseValid: result.valid
  });

  if (result.valid) {
    try {
      localStorage.setItem(LICENSE_STORAGE_KEY, rawKey.trim().toUpperCase());
    } catch {
      /* private mode - key just will not persist */
    }
    showToast('success', 'Pro unlocked: 100+ batch ZIP export and auto 86% fill enabled.');
  } else {
    showToast('error', result.message);
  }
}

function restoreLicense(): void {
  try {
    const stored = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (!stored) return;
    const result = validateLicenseKey(stored);
    if (result.valid) {
      store.set({
        licenseInput: stored,
        isPro: true,
        licenseMessage: result.message,
        licenseValid: true
      });
    }
  } catch {
    /* storage unavailable */
  }
}

/* -------------------------------------------------------------------------- */
/* Export                                                                     */
/* -------------------------------------------------------------------------- */

async function exportZip(): Promise<void> {
  const state = store.getState();
  const quota = checkBatchQuota(state.images.length, state.isPro);

  if (state.images.length === 0) {
    showToast('info', 'Add at least one image before exporting.');
    return;
  }
  if (!quota.allowed) {
    showToast('error', quota.message ?? 'Batch limit reached.');
    focusLicense();
    return;
  }

  store.set({ exporting: true });
  try {
    const preset = MARKETPLACE_PRESETS[state.preset];
    const ext = extensionForFormat(state.format);
    const files: ZipFileInput[] = [];
    const reportLines: string[] = [
      'AlgorithmShield export manifest',
      `Preset: ${preset.platform} ${preset.width}x${preset.height}`,
      `Format: ${state.format} @ ${Math.round(state.quality * 100)}% quality`,
      `Generated: ${new Date().toISOString()}`,
      ''
    ];

    for (let i = 0; i < state.images.length; i++) {
      const image = state.images[i];
      const fitted = fitIntoPreset(
        image.workingPixels,
        image.workingWidth,
        image.workingHeight,
        preset.width,
        preset.height
      );

      const blob = await pixelsToBlob(fitted.pixels, fitted.width, fitted.height, state.format, state.quality);
      let bytes = await blobToBytes(blob);
      if (state.format === 'image/jpeg') {
        bytes = stripExif(bytes); // explicit EXIF guarantee (Canvas re-encode already drops it)
      }

      const filename = generateBatchFilename(state.sku, i + 1, ext);
      files.push({ name: filename, data: bytes });
      reportLines.push(
        `${filename} <- ${image.fileName} | white ${image.report.perimeterWhiteScore}% | fill ${image.report.fillRatio}% | pop ${image.report.popScore}`
      );
    }

    files.push({ name: 'algorithmshield-report.txt', data: textEntry(reportLines.join('\n')) });

    const zip = createBatchZip(files);
    downloadBytes(zip, `${slugifySku(state.sku)}-batch.zip`, 'application/zip');
    showToast('success', `ZIP exported: ${state.images.length} image${state.images.length > 1 ? 's' : ''} (EXIF stripped, SKU-renamed).`);
  } catch (error) {
    showToast('error', error instanceof Error ? error.message : 'Export failed.');
  } finally {
    store.set({ exporting: false });
  }
}

/* -------------------------------------------------------------------------- */
/* Event delegation                                                           */
/* -------------------------------------------------------------------------- */

function handleAction(action: string, element: HTMLElement): void {
  const id = element.dataset.id ?? '';
  switch (action) {
    case 'snap-white':
      snapWhite(id);
      break;
    case 'snap-all':
      snapAll();
      break;
    case 'auto-fill':
      applyAutoFill(id);
      break;
    case 'lock-pro':
      showToast('info', 'Activate a Pro key to unlock auto 86% fill scaling.');
      focusLicense();
      break;
    case 'reset':
      resetImage(id);
      break;
    case 'remove':
      store.updateImages((images) => images.filter((image) => image.id !== id));
      if (store.getState().activeId === id) store.set({ activeId: store.getState().images[0]?.id ?? null });
      break;
    case 'select':
      store.set({ activeId: id });
      showToast('info', 'Your listing updated in the mobile grid.');
      break;
    case 'clear':
      store.clear();
      break;
    case 'toggle-sim-theme':
      store.set({ simTheme: store.getState().simTheme === 'light' ? 'dark' : 'light' });
      break;
    case 'set-preset':
      store.set({ preset: (element.dataset.preset ?? 'amazon') as MarketplaceId });
      break;
    case 'set-format':
      store.set({ format: (element.dataset.format ?? 'image/jpeg') as ExportFormat });
      break;
    case 'export-zip':
      void exportZip();
      break;
    case 'demo-license': {
      const key = generateLicenseKey(DEMO_LICENSE_SEED);
      store.set({ licenseInput: key });
      showToast('info', 'Demo Pro key inserted — press Activate.');
      break;
    }
    case 'focus-license':
      focusLicense();
      break;
    default:
      break;
  }
}

function wireEvents(): void {
  root.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!target || !root.contains(target)) return;
    const action = target.dataset.action;
    if (action) handleAction(action, target);
  });

  root.addEventListener('change', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target instanceof HTMLInputElement && target.dataset.input === 'files') {
      void addFiles(target.files);
      target.value = '';
      return;
    }

    if (target instanceof HTMLSelectElement && target.id === 'sim-position') {
      store.set({ simSlot: Number(target.value) || 0 });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.input === 'quality') {
      store.setSilent({ quality: Number(target.value) });
    }
  });

  root.addEventListener('input', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target instanceof HTMLInputElement && target.dataset.input === 'sku') {
      store.setSilent({ sku: target.value });
      // Update the filename previews in place - no re-render, so focus and
      // the caret position survive every keystroke.
      const filename = generateBatchFilename(store.getState().sku, 1, extensionForFormat(store.getState().format));
      const codes = root.querySelectorAll<HTMLElement>('#export code');
      codes.forEach((code, index) => {
        code.textContent = index === 0 ? filename : filename.replace('-main-01', '-main-02');
      });
      return;
    }

    if (target instanceof HTMLInputElement && target.dataset.input === 'quality') {
      store.setSilent({ quality: Number(target.value) });
      const label = root.querySelector<HTMLElement>('[data-quality-label]');
      if (label) label.textContent = `${Math.round(Number(target.value) * 100)}%`;
      return;
    }

    if (target instanceof HTMLInputElement && target.id === 'license-key') {
      store.setSilent({ licenseInput: target.value });
    }
  });

  root.addEventListener('submit', (event) => {
    const form = (event.target as HTMLElement | null)?.closest<HTMLFormElement>('[data-form="license"]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector<HTMLInputElement>('input[name="licenseKey"]');
    activateLicense(input?.value ?? '');
  });

  // Live quality label update also needs `input` on the range (handled above).

  // Drag & drop
  root.addEventListener('dragover', (event) => {
    const zone = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-dropzone]');
    if (!zone) return;
    event.preventDefault();
    zone.classList.add('is-over');
  });

  root.addEventListener('dragleave', (event) => {
    const zone = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-dropzone]');
    if (zone) zone.classList.remove('is-over');
  });

  root.addEventListener('drop', (event) => {
    const zone = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-dropzone]');
    if (!zone) return;
    event.preventDefault();
    zone.classList.remove('is-over');
    void addFiles(event.dataTransfer?.files ?? null);
  });

  // Stop the browser from opening files dropped outside the drop zone.
  window.addEventListener('dragover', (event) => event.preventDefault());
  window.addEventListener('drop', (event) => event.preventDefault());

  window.addEventListener('resize', () => paintPreviews(store.getState()));
}

/* -------------------------------------------------------------------------- */
/* PWA                                                                        */
/* -------------------------------------------------------------------------- */

function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* offline cache is a progressive enhancement */
    });
  });
}

/* -------------------------------------------------------------------------- */

function boot(): void {
  restoreLicense();
  wireEvents();
  subscribe();
  registerServiceWorker();
}

void boot();
